import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 120px 0;
  background: var(--gradient-surface);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 1200px;
    height: 1200px;
    border-radius: 50%;
    background: var(--gradient-primary);
    top: -600px;
    right: -300px;
    opacity: 0.05;
    animation: float 8s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-30px) rotate(5deg); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Content = styled.div`
  order: 1;
  animation: slideIn var(--transition-normal) forwards;
  
  h2 {
    font-size: 42px;
    font-weight: 800;
    margin-bottom: 24px;
    line-height: 1.2;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 18px;
    color: var(--text-secondary);
    margin-bottom: 20px;
    line-height: 1.6;
  }
`;

const IPadFrame = styled.div`
  background: var(--surface);
  border-radius: 40px;
  padding: 20px;
  box-shadow: var(--shadow-lg);
  width: 700px;
  max-width: 100%;
  order: 2;
  position: relative;
  transition: all var(--transition-normal);
  border: 1px solid var(--border);
  animation: fadeIn var(--transition-normal) forwards;

  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-primary);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    border-radius: 4px 4px 0 0;
  }

  @media (max-width: 968px) {
    margin: 40px auto;
  }
`;

const Screen = styled.div`
  background: var(--background);
  border-radius: 30px;
  overflow: auto;
  padding: 24px;
  height: 600px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
  transition: all var(--transition-normal);
`;

const ExportModal = styled.div`
  background: var(--surface);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  padding: 32px;
  width: 480px;
  pointer-events: none;
  border: 1px solid var(--border);
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .close {
    color: var(--text-secondary);
    font-size: 24px;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all var(--transition-normal);

    &:hover {
      background: var(--surface-hover);
      color: var(--error);
    }
  }
`;

const ColumnList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
  max-height: 380px;
  overflow-y: auto;
  padding-right: 16px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--background);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 4px;
    
    &:hover {
      background: var(--primary-dark);
    }
  }
`;

const ColumnItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  transition: all var(--transition-normal);

  &:hover {
    transform: translateX(4px);
    background: var(--surface-hover);
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    border: 2px solid var(--border);
    border-radius: 4px;
    cursor: pointer;
    position: relative;
    appearance: none;
    background: var(--surface);
    transition: all var(--transition-normal);

    &:checked {
      background: var(--primary);
      border-color: var(--primary);

      &::after {
        content: '✓';
        color: white;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 12px;
      }
    }

    &:hover {
      border-color: var(--primary);
    }
  }

  span {
    font-size: 14px;
    color: var(--text-primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  
  ${props => props.primary ? `
    background: var(--gradient-primary);
    color: white;
    border: none;

    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-primary);
    }
  ` : `
    background: var(--surface);
    color: var(--text-secondary);
    border: 1px solid var(--border);

    &:hover {
      background: var(--surface-hover);
      color: var(--text-primary);
    }
  `}
`;

const DataExports = () => {
  const columns = [
    'Name',
    'Provider',
    'Cycle',
    'Price',
    'Payment method',
    'Type',
    'Start date',
    'Due date',
    'Tags'
  ];

  return (
    <Section>
      <Container>
        <Content>
          <h2>Customizable Data Exports</h2>
          <p>
            Easily export your subscription data with customizable fields.
          </p>
          <p>
            Perfect for sharing or keeping offline records.
          </p>
        </Content>

        <IPadFrame>
          <Screen>
            <ExportModal>
              <ModalHeader>
                <h3>Export subscriptions</h3>
                <span className="close">×</span>
              </ModalHeader>

              <div>
                <h4 style={{ 
                  marginBottom: '16px', 
                  color: 'var(--text-secondary)', 
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Select columns to export
                </h4>
                <ColumnList>
                  {columns.map(column => (
                    <ColumnItem key={column}>
                      <input type="checkbox" defaultChecked />
                      <span>{column}</span>
                    </ColumnItem>
                  ))}
                </ColumnList>
              </div>

              <ButtonGroup>
                <Button primary>Export</Button>
                <Button>Cancel</Button>
              </ButtonGroup>
            </ExportModal>
          </Screen>
        </IPadFrame>
      </Container>
    </Section>
  );
};

export default DataExports; 